import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import podiumImage from '@/images/gift_subs_podium.jpg';
import CardGiftcardIcon from '@/material-icons/400-24px/card_giftcard.svg?react';
import { fetchTopGifters } from 'mastodon/actions/rooms';
import { Icon } from 'mastodon/components/icon';
import { GiftSubsModal } from 'mastodon/features/rooms/components/gift_subs_modal';
import type {
  TopGifterEntry,
  TopGiftersBoard,
} from 'mastodon/features/rooms/top_gifters';
import {
  climbHint,
  formatResetCountdown,
  formatSubs,
  giftTier,
  hiddenGifterCount,
  isUsableBoard,
  listEntries,
  marqueeDuration,
  ordinal,
  shouldRenderTicker,
  tickerEntries,
} from 'mastodon/features/rooms/top_gifters';
import { me } from 'mastodon/initial_state';

interface Props {
  roomId: string;
  hostName: string;
  /** Whether gifting is switched on for this instance (from the room JSON). */
  giftSubsEnabled?: boolean;
  /**
   * Bumped by the parent whenever a `gifted_subs` line lands in chat, which is
   * how the board goes live: the gift announces itself through the chat poll
   * that already exists, so a new gifter appears without this component having
   * to poll an endpoint of its own on a timer.
   */
  refreshToken?: number;
}

const displayNameOf = (entry: TopGifterEntry): string =>
  entry.account.display_name || entry.account.username;

/** The 1/2/3 medal, or a plain number for everyone below the podium. */
const RankBadge: React.FC<{ rank: number }> = ({ rank }) => {
  if (rank > 3) return <span className='top-gifters__rank'>{rank}</span>;

  return (
    <span
      className={`top-gifters__rank top-gifters__rank--medal top-gifters__rank--${rank}`}
      aria-hidden='true'
    >
      {rank}
    </span>
  );
};

/**
 * Ticket 75 — the top-gifters roller that sits on top of the room chat, and the
 * board it expands into.
 *
 * The standings are for the room's HOST across the whole period rather than for
 * this room, and they reset by themselves when the month turns: the server
 * derives them from the purchases inside the current month, so there is no
 * counter anywhere that has to be zeroed on the 1st.
 */
export const TopGiftersTicker: React.FC<Props> = ({
  roomId,
  hostName,
  giftSubsEnabled,
  refreshToken = 0,
}) => {
  const [board, setBoard] = useState<TopGiftersBoard | null>(null);
  const [open, setOpen] = useState(false);
  const [giftOpen, setGiftOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const request = { live: true };

    void (async () => {
      try {
        const data = await fetchTopGifters(roomId);
        // A board we cannot draw is discarded here rather than defended against
        // at every use site below: this renders inside the chat panel, and an
        // entry that throws mid-render would take the chat down with it.
        if (request.live) setBoard(isUsableBoard(data) ? data : null);
      } catch {
        if (request.live) setBoard(null);
      }
    })();

    return () => {
      request.live = false;
    };
  }, [roomId, refreshToken]);

  // Click-outside and Escape both close the board. It is drawn OVER the
  // messages rather than pushing them down, so leaving it open by accident
  // would hide chat — which is precisely the complaint ticket 29 was about.
  // The difference is that this one is opened deliberately and closes on any
  // of three gestures.
  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const handleToggle = useCallback(() => {
    setOpen((wasOpen) => !wasOpen);
  }, []);

  const handleGiftOpen = useCallback(() => {
    setGiftOpen(true);
    setOpen(false);
  }, []);

  const handleGiftClose = useCallback(() => {
    setGiftOpen(false);
  }, []);

  const ticker = useMemo(() => (board ? tickerEntries(board) : []), [board]);
  const rows = useMemo(() => (board ? listEntries(board) : []), [board]);
  const hint = useMemo(() => (board ? climbHint(board, me) : null), [board]);

  if (!shouldRenderTicker(board, giftSubsEnabled === true) || !board)
    return null;

  const hidden = hiddenGifterCount(board);
  const canGift =
    giftSubsEnabled === true && !!me && me !== board.host_account_id;
  // Two copies of the track back to back is what makes the loop seamless: the
  // animation slides exactly one copy's width and restarts, so the join is
  // never visible and the ticket's "never pause or stop" holds literally.
  const duration = marqueeDuration(ticker.length);

  return (
    <div className='top-gifters' ref={containerRef} data-testid='top-gifters'>
      <button
        type='button'
        className='top-gifters__bar'
        onClick={handleToggle}
        aria-expanded={open}
        aria-label={`Top gifters this month. ${board.total_gifters} ${
          board.total_gifters === 1 ? 'person has' : 'people have'
        } gifted ${formatSubs(board.total_subs)}. Open the full board.`}
        data-testid='top-gifters-bar'
      >
        <span className='top-gifters__bar-icon' aria-hidden='true'>
          <Icon id='' icon={CardGiftcardIcon} />
        </span>
        <span className='top-gifters__viewport'>
          {ticker.length === 0 ? (
            <span className='top-gifters__empty-inline'>
              Be the first to gift a sub
            </span>
          ) : (
            <span
              className='top-gifters__track'
              style={{ animationDuration: `${duration}s` }}
              data-testid='top-gifters-track'
            >
              {[0, 1].map((copy) => (
                <span
                  className='top-gifters__track-copy'
                  key={copy}
                  aria-hidden={copy === 1 ? 'true' : undefined}
                >
                  {ticker.map((entry) => (
                    <span
                      className='top-gifters__chip'
                      key={`${copy}-${entry.account.id}`}
                    >
                      <RankBadge rank={entry.rank} />
                      <span className='top-gifters__chip-name'>
                        {displayNameOf(entry)}
                      </span>
                      <span
                        className={`top-gifters__chip-subs top-gifters__chip-subs--${giftTier(entry.subs)}`}
                      >
                        {entry.subs}
                      </span>
                    </span>
                  ))}
                </span>
              ))}
            </span>
          )}
        </span>
        <span className='top-gifters__chevron' aria-hidden='true'>
          {open ? '▲' : '▼'}
        </span>
      </button>

      {open && (
        <div className='top-gifters__panel' data-testid='top-gifters-panel'>
          <div className='top-gifters__panel-head'>
            <div className='top-gifters__panel-title'>Top Gifters</div>
            <div className='top-gifters__panel-sub'>
              {formatResetCountdown(board.resets_in_seconds)}
            </div>
          </div>

          <img
            className='top-gifters__podium'
            src={podiumImage}
            alt=''
            loading='lazy'
          />

          {rows.length === 0 ? (
            <div className='top-gifters__empty'>
              Nobody has gifted a sub to {hostName} this month yet.
            </div>
          ) : (
            <>
              <ol className='top-gifters__list'>
                {rows.map((entry) => (
                  <li
                    className={`top-gifters__row${entry.rank <= 3 ? ' top-gifters__row--podium' : ''}`}
                    key={entry.account.id}
                  >
                    <RankBadge rank={entry.rank} />
                    <img
                      className='top-gifters__avatar'
                      src={entry.account.avatar}
                      alt=''
                      loading='lazy'
                    />
                    <span
                      className='top-gifters__name'
                      title={entry.account.acct}
                    >
                      {displayNameOf(entry)}
                    </span>
                    <span
                      className={`top-gifters__subs top-gifters__subs--${giftTier(entry.subs)}`}
                    >
                      {entry.subs}
                      <Icon id='' icon={CardGiftcardIcon} />
                    </span>
                  </li>
                ))}
              </ol>
              {hidden > 0 && (
                <div className='top-gifters__more'>
                  and {hidden} more {hidden === 1 ? 'gifter' : 'gifters'}
                </div>
              )}
            </>
          )}

          {hint && canGift && (
            <div className='top-gifters__hint'>
              Gift <strong>{formatSubs(hint.subs)}</strong> to take{' '}
              {ordinal(hint.rank)}
            </div>
          )}

          {canGift && (
            <button
              type='button'
              className='top-gifters__cta'
              onClick={handleGiftOpen}
              data-testid='top-gifters-cta'
            >
              <Icon id='' icon={CardGiftcardIcon} /> Gift Sub
            </button>
          )}
        </div>
      )}

      {giftOpen && (
        <GiftSubsModal
          roomId={roomId}
          hostName={hostName}
          onClose={handleGiftClose}
        />
      )}
    </div>
  );
};
